import { ReactComponent as ChevronUpLight } from "assets/images/icons/icn_pagination_chevron_up_light.svg";
import { ReactComponent as ChevronDownLight } from "assets/images/icons/icn_pagination_chevron_down_light.svg";

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

const DropdownInputGroup = ({
  value,
  options,
  onChange,
  children,
  width = 300,
  selectWidth = 120,
  error = false,
  helperText = "",
  disabled = false,
  inputDisabled = false,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuWidth, setMenuWidth] = useState(selectWidth);
  const groupRef = useRef(null);
  const menuOpen = Boolean(anchorEl);
  const selectedLabel = options.find((option) => option.value === value)?.label || "";
  const borderColor = error
    ? theme.palette.primary.redPrimary
    : theme.palette.primary.greyFifth;

  useEffect(() => {
    setMenuWidth(selectWidth);
  }, [selectWidth]);

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ width }}>
      <Box
        ref={groupRef}
        sx={{
          width,
          height: 31,
          display: "flex",
          alignItems: "stretch",
          border: `1px solid ${borderColor}`,
          borderRadius: theme.shape.customBorderRadius[1],
          backgroundColor: theme.palette.primary.white,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={handleOpen}
          sx={{
            width: selectWidth,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <Typography
            sx={{
              minWidth: 0,
              flex: 1,
              px: 1.5,
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[3],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedLabel}
          </Typography>
          <Box
            sx={{
              width: 32,
              height: "100%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette.primary.main,
            }}
          >
            {menuOpen ? <ChevronUpLight /> : <ChevronDownLight />}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            borderLeft: `1px solid ${borderColor}`,
            backgroundColor: inputDisabled
              ? theme.palette.primary.greyThread
              : theme.palette.primary.white,
          }}
        >
          {children}
        </Box>
      </Box>

      {helperText && (
        <Typography
          sx={{
            color: theme.palette.primary.redPrimary,
            fontSize: theme.typography.customFontSize[1],
            lineHeight: "18px",
            ml: `${selectWidth + 12}px`,
            mt: "2px",
            mb: "8px",
          }}
        >
          {helperText}
        </Typography>
      )}

      {menuOpen && (
        <Popper
          open={menuOpen}
          anchorEl={groupRef.current}
          placement="bottom-start"
          disablePortal={false}
          style={{ zIndex: theme.zIndex.modal, width: menuWidth }}
          modifiers={[
            {
              name: "offset",
              options: { offset: [0, 8] },
            },
          ]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={3}
              sx={{
                width: menuWidth,
                borderRadius: 3,
                boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
              }}
            >
              <List sx={{ py: "8px", px: "10px" }}>
                {options.map((option) => (
                  <ListItem
                    key={option.value}
                    button
                    selected={value === option.value}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(option);
                      handleClose();
                    }}
                    sx={{
                      borderRadius: theme.shape.customBorderRadius[0],
                      color: theme.palette.primary.main,
                      px: "10px",
                      py: "8px",
                      mb: "5px",
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.primary.lightBlue,
                      },
                      "&:hover": {
                        backgroundColor: theme.palette.primary.lightBlue,
                      },
                    }}
                  >
                    <ListItemText
                      primary={option.label}
                      primaryTypographyProps={{
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[3],
                        whiteSpace: "nowrap",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};

export default DropdownInputGroup;
